"use client";

import { defaultSupportAgentId } from "@/constants";
import { clsx } from "clsx";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useState } from "react";
import { Orb } from "@ui/components/core/Orb/Orb";
import { pxToRem } from "@ui/lib/utils";
import dynamic from "next/dynamic";
import { useFloatingAgentStore } from "./FloatingAgentProvider";
import { useDebounce } from "use-debounce";
import { AnimatePresence } from "motion/react";
import { useSupportAgentConfig } from "@/app/_components/SupportAgent/ConfigProvider";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconChevronLeftSmall from "@ui/central-icons/icons/IconChevronLeftSmall";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

const FloatingAgentInner = dynamic(() => import("./FloatingAgentInner").then(mod => mod.FloatingAgentInner), {
  ssr: false,
  loading: () => null,
});

const ORB_SIZE = {
  collapsed: 36,
  voice: 160,
  text: 40,
};

function FloatingAgentOrb(
  props: Omit<
    React.ComponentPropsWithoutRef<typeof Orb>,
    "conversation" | "texture" | "image" | "width" | "height" | "exitDuration"
  >,
) {
  const image = useMemo(() => ({ sizes: pxToRem(ORB_SIZE.voice) }), []);
  const conversation = useFloatingAgentStore(state => state.session?.conversation);

  return (
    <Orb
      {...props}
      texture='agents-2'
      image={image}
      width={ORB_SIZE.voice}
      height={ORB_SIZE.voice}
      exitDuration={0.8}
      conversation={conversation}
    />
  );
}

function IconCall(props: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg viewBox='0 0 24 24' aria-hidden {...props}>
      <path
        fill='currentColor'
        d='M5.723 3C4.248 3 2.927 4.206 3.097 5.796c.251 2.36.993 4.662 2.19 6.723a17.1 17.1 0 0 0 6.195 6.195c2.075 1.205 4.348 1.913 6.69 2.172 1.598.177 2.829-1.143 2.829-2.636v-1.753a2.75 2.75 0 0 0-1.974-2.639l-1.682-.495a2.69 2.69 0 0 0-2.702.719c-.377.392-.914.452-1.285.212a12.3 12.3 0 0 1-3.652-3.651c-.24-.372-.18-.908.213-1.285a2.69 2.69 0 0 0 .718-2.702l-.494-1.682A2.75 2.75 0 0 0 7.504 3z'
      />
    </svg>
  );
}

function OrbIcon({
  isVisible,
  layoutDependency,
  icon,
}: {
  isVisible: boolean;
  layoutDependency: string;
  icon: "call" | "back";
}) {
  return (
    <m.div
      layout
      layoutDependency={layoutDependency}
      className={clsx(
        "tw-absolute tw-top-1/2 tw-left-1/2 tw-rounded-full tw-bg-white",
        icon === "call" ? "tw-size-12 -tw-ml-6 -tw-mt-6" : "tw-size-4.5 tw-ml-[-0.5625rem] tw-mt-[-0.5625rem]",
      )}
      style={{
        boxShadow: "0 0 1px 0 rgba(0, 0, 0, 0.40), 0 1px 1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px 0 rgba(0, 0, 0, 0.04)",
      }}
      initial={false}
      animate={isVisible ? { opacity: 1, transition: { delay: 0.2 } } : { opacity: 0 }}
    >
      <m.div
        layout
        layoutDependency={layoutDependency}
        className='tw-absolute tw-top-1/2 tw-left-1/2 -tw-mt-2.5 -tw-ml-2.5'
        initial={false}
        animate={icon === "call" ? { opacity: 1 } : { opacity: 0 }}
      >
        <IconCall className='tw-size-5' />
      </m.div>
      <m.div
        layout
        layoutDependency={layoutDependency}
        className='tw-absolute tw-top-1/2 tw-left-1/2'
        style={{ marginTop: pxToRem(-9), marginLeft: pxToRem(-9) }}
        initial={false}
        animate={icon === "call" ? { opacity: 0 } : { opacity: 1 }}
      >
        <IconChevronLeftSmall className={clsx("tw-size-4.5", iconStrokeFix)} />
      </m.div>
    </m.div>
  );
}

function Trigger({
  title: customTitle,
  isOpen,
  setIsOpen,
}: {
  title?: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const id = useId();
  const t = useTranslations("ClientKeys.FloatingAgent");
  const session = useFloatingAgentStore(state => state.session);
  const sessionStatus = session?.conversation.status ?? "disconnected";
  const connected = sessionStatus !== "disconnected";
  const voiceIsActive = session?.voiceIsActive ?? false;
  const agentMode = session?.agentMode;

  const title = (() => {
    if (customTitle) {
      return customTitle;
    }
    if (session?.sessionIsActive) {
      return voiceIsActive ? t("trigger_title") : t("trigger_title_text");
    }
    return t("trigger_title");
  })();

  const subTitle = (() => {
    if (!connected) {
      return null;
    }
    if (sessionStatus === "connecting") {
      return t("status.connecting");
    }
    if (sessionStatus === "disconnecting") {
      return t("status.disconnecting");
    }
    if (voiceIsActive) {
      if (agentMode !== null) {
        if (agentMode === "speaking") {
          return t("status.speaking");
        }
        if (agentMode === "thinking") {
          return t("status.thinking");
        }
        return t("status.listening");
      }
      return t("status.connected");
    }
    return t("status.connected");
  })();

  const message = useMemo(() => {
    return {
      text: subTitle,
      className: clsx("tw-type-2xs", connected ? "tw-font-medium tw-text-blue-500" : "tw-text-gray-500"),
    };
  }, [connected, subTitle]);

  return (
    <m.button
      data-focus-outline=''
      data-hover=''
      layout
      layoutDependency={isOpen}
      type='button'
      className={clsx(
        "tw-h-12 tw-py-1.5 tw-pl-12 sm:tw-h-14 sm:tw-py-2.5 sm:tw-pl-14 tw-pr-5 tw-flex tw-items-center tw-text-left tw-outline-none",
        isOpen && "tw-absolute tw-right-0 tw-bottom-0",
      )}
      onClick={() => {
        setIsOpen(true);
        captureEvent(EventName.marketing_site_convai_agent_expanded);
      }}
      initial={false}
      animate={isOpen ? { opacity: 0 } : { opacity: 1, transition: { delay: 0.3 } }}
      inert={isOpen}
      aria-labelledby={`${id}-trigger-title`}
      aria-describedby={message.text ? `${id}-trigger-subtitle` : undefined}
    >
      <div>
        <div id={`${id}-trigger-title`} className='tw-type-sm sm:tw-type-base tw-text-black'>
          {title}
        </div>
        {message.text && (
          <div id={`${id}-trigger-subtitle`} className='tw-mt-1.5 sm:tw-mt-2'>
            <StatusMessage message={message} />
          </div>
        )}
      </div>
    </m.button>
  );
}

function StatusMessage({ message }: { message: { text: string | null; className: string } }) {
  const [state, setState] = useState({ message, key: 0 });
  const [debouncedMessage] = useDebounce(message, 200);

  useLayoutEffect(() => {
    if (!debouncedMessage?.text) return;
    setState(state => ({
      ...state,
      message: debouncedMessage,
      key:
        debouncedMessage.text === state.message.text && debouncedMessage.className === state.message.className
          ? state.key
          : state.key + 1,
    }));
  }, [debouncedMessage]);

  return (
    <AnimatePresence initial={false} mode='wait'>
      <m.div
        key={state.key}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className={state.message.className}
      >
        {state.message.text}
      </m.div>
    </AnimatePresence>
  );
}

export function FloatingAgent() {
  const [showTerms, setShowTerms] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [_isOpen, setIsOpen] = useState(false);
  const isOpen = _isOpen && isReady;
  const [orbIsAnimated, setOrbIsAnimated] = useState(false);
  const [mode, setMode] = useState<"text" | "voice">("voice");
  const [displayMode, setDisplayMode] = useState<"text" | "voice">("voice");
  const [isRendered, setIsRendered] = useState(false);
  const { registerStartCallAction, unregisterStartCallAction, config } = useSupportAgentConfig();

  const agentId = config.customAgentId || defaultSupportAgentId;
  const position = config.customPosition;
  const triggerTitle = config.customTitle;

  function renderInnerComponent() {
    setIsRendered(true);
  }

  if (_isOpen && !isRendered) {
    renderInnerComponent();
  }

  const onReady = useCallback(() => {
    setIsReady(true);
  }, []);

  const sessionStatus = useFloatingAgentStore(state => state.session?.status) ?? "disconnected";
  const sessionIsActive = useFloatingAgentStore(state => state.session?.sessionIsActive) ?? false;

  const showOrbIcon =
    isOpen && (sessionStatus === "disconnected" || (sessionIsActive && mode === "voice" && displayMode === "text"));

  // Register the start call action with the provider
  useEffect(() => {
    function startCall() {
      setIsOpen(true);
    }
    registerStartCallAction(startCall);
    return () => {
      unregisterStartCallAction(startCall);
    };
  }, [registerStartCallAction, unregisterStartCallAction]);

  return (
    <div
      className={clsx(
        "tw-@container tw-fixed tw-z-50 tw-bottom-4 tw-pointer-events-none tw-flex",
        position === "center"
          ? "tw-justify-center"
          : "tw-justify-center sm:tw-right-4 sm:tw-justify-end sm:!tw-left-auto",
      )}
      style={{
        width: pxToRem(380),
        maxWidth: "calc(100% - 2rem)",
        left: "calc(50% - min(11.875rem, (100% - 2rem) / 2))",
      }}
    >
      <m.div
        layout
        layoutDependency={isOpen}
        className='tw-pointer-events-auto tw-relative tw-isolate tw-bg-black/15 has-[[data-focus-outline]:focus-visible]:tw-outline-focus tw-flex'
        style={{ borderRadius: 28, width: isOpen ? "100%" : "fit-content", height: isOpen ? pxToRem(480) : undefined }}
        onMouseEnter={renderInnerComponent}
        onTouchStart={renderInnerComponent}
      >
        {/* Outer ring, separate element so it doesn't distort during layout animation */}
        <m.div
          layout
          layoutDependency={isOpen}
          className='tw-absolute -tw-z-10 -tw-inset-px tw-bg-black/5'
          style={{ borderRadius: 28 + 1 }}
        />

        <m.div
          layout
          layoutDependency={isOpen}
          className='tw-relative tw-w-full tw-bg-white tw-overflow-clip tw-transition-colors has-[[data-hover]:hover]:tw-bg-gray-25'
          style={{ borderRadius: 28, boxShadow: "0 6px 16px rgb(78 50 23 / 0.04)" }}
        >
          <m.div
            key='orb'
            layout
            layoutDependency={`${isOpen}-${displayMode}`}
            className={clsx(
              "tw-pointer-events-none tw-absolute tw-z-50",
              !isOpen && "tw-left-2 tw-bottom-2 tw-size-8 sm:tw-left-2.5 sm:tw-bottom-2.5 sm:tw-size-9",
              isOpen && displayMode === "voice" && "tw-top-1/2 tw-left-1/2 tw-size-40 -tw-mt-32 -tw-ml-20",
              isOpen && displayMode === "text" && "tw-left-3 tw-top-3 tw-size-10",
            )}
            onLayoutAnimationComplete={isOpen ? () => setOrbIsAnimated(true) : undefined}
            onLayoutAnimationStart={!isOpen ? () => setOrbIsAnimated(false) : undefined}
          >
            <div className='tw-absolute tw-inset-0 tw-rounded-full tw-overflow-hidden'>
              <FloatingAgentOrb
                animated={orbIsAnimated || sessionIsActive}
                noiseScale={
                  isOpen
                    ? displayMode === "voice"
                      ? 1
                      : ORB_SIZE.text / ORB_SIZE.voice
                    : ORB_SIZE.collapsed / ORB_SIZE.voice
                }
              />
            </div>

            <OrbIcon
              isVisible={showOrbIcon}
              layoutDependency={`${isOpen}-${displayMode}`}
              icon={displayMode === "voice" ? "call" : "back"}
            />
          </m.div>

          <Trigger key='trigger' title={triggerTitle} isOpen={isOpen} setIsOpen={setIsOpen} />

          <m.div
            layout
            layoutDependency={isOpen}
            key='main'
            className={clsx(
              "tw-absolute tw-bottom-0",
              position === "center" ? "tw-left-1/2" : "tw-left-1/2 sm:tw-right-0 sm:tw-left-auto sm:!tw-ml-0",
            )}
            style={{ width: "100cqw", height: pxToRem(480), marginLeft: "-50cqw" }}
            inert={!isOpen}
          >
            {isRendered && (
              <FloatingAgentInner
                agentId={agentId}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                onReady={onReady}
                displayMode={displayMode}
                setDisplayMode={setDisplayMode}
                setMode={setMode}
                showTerms={showTerms}
                setShowTerms={setShowTerms}
              />
            )}
          </m.div>
        </m.div>
      </m.div>
    </div>
  );
}
